//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import {
  CampaignType,
  ICampaign,
  ICampaignAutocompleteResponse,
  ICampaignMediaResponse,
  ICampaignSearchRequestBody,
  IGetCampaignAutocompleteRequest,
  IGetCampaignAutocompleteResponse,
  IGetCampaignExportRequest,
  IGetCampaignMediaRequest,
  IGetCampaignMediaResponse,
  IGetCampaignResponse,
  IGetClientCampaignsRequest,
  IGetClientCampaignsResponse,
} from '@apis/campaigns/campaignsApiModel';
import { deserializeCampaign } from '@apis/campaigns/campaignsApiUtils';
import { deserializeMediaItem } from '@apis/media-library/mediaLibraryApiUtils';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const CAMPAIGNS_DEFAULT_ENDPOINT = '/api/v1/campaigns';

const campaignsFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'influencers_gateway',
    `${CAMPAIGNS_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );
};

/**
 * Gets all campaigns of a specified campaign type
 *
 * GET /api/v1/campaigns/:campaignType
 */
export const getClientCampaigns = async (
  campaignType: CampaignType,
  req: IGetClientCampaignsRequest,
): Promise<ICampaign[]> => {
  const params = makeParams(req);
  const res = await campaignsFetch(`/${campaignType}?${params}`);

  const clientCampaignsResponse = await res.json<IGetClientCampaignsResponse>(
    (res) => ({
      data: {
        campaigns: res.data.campaigns.map((campaign) =>
          deserializeCampaign(campaign),
        ),
      },
    }),
  );

  return clientCampaignsResponse.data.campaigns;
};

/**
 * Gets campaign by id
 *
 * GET /api/v1/campaigns/:campaignType/:campaignId
 */
export const getCampaign = async (
  campaignType: CampaignType,
  campaignId: string,
): Promise<ICampaign | undefined> => {
  const res = await campaignsFetch(`/${campaignType}/${campaignId}`);

  const clientCampaignResponse = await res.json<IGetCampaignResponse>(
    (res) => ({
      data: res.data ? deserializeCampaign(res.data) : undefined,
    }),
  );

  return clientCampaignResponse.data;
};

/**
 * Gets media associated with specified campaign
 *
 * POST /api/v1/campaigns/:campaignType/:campaignId/media
 */
export const getCampaignMedia = async (
  campaignType: CampaignType,
  campaignId: string,
  req: IGetCampaignMediaRequest,
  body: ICampaignSearchRequestBody,
): Promise<ICampaignMediaResponse> => {
  const params = makeParams(req);
  const res = await campaignsFetch(
    `/${campaignType}/${campaignId}/media?${params}`,
    {
      body: JSON.stringify(body),
      method: 'POST',
    },
  );

  const campaignMediaResponse = await res.json<IGetCampaignMediaResponse>(
    (res) => ({
      data: {
        ...res.data,
        media: res.data.media.map((media) => deserializeMediaItem(media)),
      },
    }),
  );

  return campaignMediaResponse.data;
};

/**
 * Gets autocomplete suggestions for a specified campaign
 *
 * POST /api/v1/campaigns/:campaignType/:campaignId/autocomplete
 */
export const getCampaignAutocomplete = async (
  campaignType: CampaignType,
  campaignId: string,
  req: IGetCampaignAutocompleteRequest,
  body: ICampaignSearchRequestBody,
): Promise<ICampaignAutocompleteResponse> => {
  const params = makeParams(req);
  const res = await campaignsFetch(
    `/${campaignType}/${campaignId}/autocomplete?${params}`,
    {
      body: JSON.stringify(body),
      method: 'POST',
    },
  );

  const campaignAutocompleteResponse =
    await res.json<IGetCampaignAutocompleteResponse>();

  return campaignAutocompleteResponse.data;
};

/**
 * Gets a zip file containing a summary and content for a specified campaign
 *
 * POST /api/v1/campaigns/:campaignType/:campaignId/export.zip
 */
export async function getCampaignExport(
  campaignType: CampaignType,
  campaignId: string,
  req: IGetCampaignExportRequest,
  body: ICampaignSearchRequestBody,
): Promise<Blob> {
  const params = makeParams(req);
  const res = await campaignsFetch(
    `/${campaignType}/${campaignId}/export.zip?${params}`,
    {
      body: JSON.stringify(body),
      method: 'POST',
    },
  );

  return res.blob();
}
